import { useEffect } from 'react'

/**
 * Lightweight per-page SEO hook for projects published via Coderblock.
 *
 * The static prerender step (scripts/prerender.js) injects baseline meta
 * tags from seo.config.json — this hook adds runtime overrides for SPAs
 * where the same page mounts under different states (e.g. detail pages
 * that read the slug from the URL).
 *
 * Usage:
 *   useSEO({
 *     title: 'My Awesome Product',
 *     description: 'Buy this thing right now',
 *     ogImage: 'https://cdn.example.com/og/product.jpg',
 *   })
 *
 * For best SEO results, also call `usePrerenderReady()` once the page
 * content is mounted.
 */
export interface SEOProps {
  title?: string
  description?: string
  keywords?: string
  canonicalUrl?: string
  ogImage?: string
  ogType?: string
  noIndex?: boolean
}

const setMeta = (name: string, content: string, isProperty = false) => {
  if (!content) return
  const attr = isProperty ? 'property' : 'name'
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, name)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

const setLink = (rel: string, href: string) => {
  if (!href) return
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage,
  ogType = 'website',
  noIndex = false,
}: SEOProps) => {
  useEffect(() => {
    if (typeof document === 'undefined') return

    if (title) document.title = title
    if (description) setMeta('description', description)
    if (keywords) setMeta('keywords', keywords)

    setMeta(
      'robots',
      noIndex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1',
    )

    if (title) {
      setMeta('og:title', title, true)
      setMeta('twitter:title', title)
    }
    if (description) {
      setMeta('og:description', description, true)
      setMeta('twitter:description', description)
    }
    setMeta('og:type', ogType, true)
    if (ogImage) {
      setMeta('og:image', ogImage, true)
      setMeta('twitter:image', ogImage)
    }
    setMeta('twitter:card', 'summary_large_image')

    if (canonicalUrl) {
      setMeta('og:url', canonicalUrl, true)
      setLink('canonical', canonicalUrl)
    }
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, noIndex])
}

export default useSEO
